import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Wifi, Copy, CheckCircle2, Send, Mail, Star, Lock, Gift, PartyPopper, TimerReset, AlertTriangle, MapPin, Coffee, UserPlus } from "lucide-react";
import { api, API } from "../lib/api";
import { useLang } from "../i18n";
import { CraftLogo, LangSwitcher, BannerCard, LabeledStepper, FeatureRow, CraftFooter } from "../components/Shared";

const TYPE_ICON = { wifi: Wifi, promo: Gift, survey: Star, loyalty: Coffee, lead: UserPlus, review: Star, custom: Gift };

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FBF3E4]">
    <div className="max-w-[430px] mx-auto min-h-screen flex flex-col">{children}</div>
  </div>
);

const Header = () => (
  <div className="flex items-center justify-between px-5 pt-5 pb-3">
    <CraftLogo />
    <LangSwitcher />
  </div>
);

function Countdown({ expiresAt, onExpire }) {
  const [left, setLeft] = useState(Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000)));
  useEffect(() => {
    const iv = setInterval(() => {
      const s = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setLeft(s);
      if (s <= 0) { clearInterval(iv); onExpire && onExpire(); }
    }, 1000);
    return () => clearInterval(iv);
  }, [expiresAt, onExpire]);
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return <span data-testid="wifi-timer" className="font-display font-black text-4xl tabular-nums text-[#FF0000]">{mm}:{ss}</span>;
}

function TelegramIdentify({ session, telegram, onVerified }) {
  const { t } = useLang();
  const [state, setState] = useState("idle");
  const [tok, setTok] = useState(null);
  const pollRef = useRef(null);

  const start = async () => {
    // Открываем окно синхронно в момент клика — иначе браузер блокирует popup после await
    let tgWin = null;
    try { tgWin = window.open("", "_blank"); } catch (e) { /* popup blocked */ }
    try {
      const { data } = await api.post("/public/verify/telegram/start", { session_id: session });
      setTok(data);
      setState("waiting");
      if (data.link) {
        if (tgWin && !tgWin.closed) tgWin.location.href = data.link;
        else window.location.href = data.link;
      } else if (tgWin) { try { tgWin.close(); } catch (e) { /* noop */ } }
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/public/verify/telegram/status/${data.token}`);
          if (st.status === "verified") { clearInterval(pollRef.current); setState("verified"); onVerified({ phone: st.phone }); }
          if (st.status === "expired") { clearInterval(pollRef.current); setState("expired"); }
        } catch (e) { /* noop */ }
      }, 2500);
    } catch (e) { if (tgWin) { try { tgWin.close(); } catch (e2) { /* noop */ } } toast.error(t("server_error")); }
  };
  useEffect(() => () => clearInterval(pollRef.current), []);

  const simulate = async () => {
    try {
      await api.post("/public/verify/telegram/simulate", { token: tok.token });
    } catch (e) { toast.error(t("server_error")); }
  };

  if (state === "verified")
    return <div className="flex items-center gap-2 justify-center py-4 text-green-600 font-bold" data-testid="tg-verified"><CheckCircle2 /> {t("verified")}</div>;

  return (
    <div className="space-y-3">
      {state === "idle" && (
        <button data-testid="tg-share-btn" onClick={start}
          className="w-full min-h-[56px] rounded-2xl bg-[#229ED9] hover:bg-[#1e8fc4] text-white font-bold text-base flex items-center justify-center gap-2.5 shadow-[0_4px_14px_rgba(34,158,217,0.4)] transition-transform active:scale-[0.97]">
          <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Send size={16} /></span>
          {t("share_tg")}
        </button>
      )}
      {state === "waiting" && (
        <>
          <div className="flex items-center justify-center gap-2 py-3 text-gray-600 font-semibold text-sm">
            <span className="w-2 h-2 rounded-full bg-[#FEBC10] animate-pulse" /> {t("waiting_tg")}
          </div>
          <p className="text-xs text-gray-500 text-center">{t("tg_hint")}</p>
          {tok?.link && (
            <a href={tok.link} target="_blank" rel="noreferrer" className="craft-btn craft-btn-ghost text-sm" data-testid="tg-open-again">Telegram</a>
          )}
          {telegram?.test_mode && (
            <button data-testid="tg-simulate-btn" onClick={simulate} className="w-full text-xs font-bold text-gray-400 underline py-2">
              {t("test_verify")}
            </button>
          )}
        </>
      )}
      {state === "expired" && (
        <div className="text-center space-y-3">
          <p className="text-sm font-bold text-[#FF0000]">{t("code_expired")}</p>
          <button className="craft-btn" onClick={start}>{t("again")}</button>
        </div>
      )}
    </div>
  );
}

function EmailIdentify({ session, onVerified }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [tok, setTok] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/verify/email/send", { session_id: session, email });
      setTok(data.token);
    } catch (e) {
      toast.error(e.response?.data?.detail === "invalid_email" ? t("invalid_email") : t("server_error"));
    }
    setBusy(false);
  };
  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/verify/email/check", { token: tok, code });
      onVerified({ email: data.email });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "invalid_code" ? t("invalid_code") : d === "code_expired" ? t("code_expired") : d === "too_many_attempts" ? t("too_many") : t("server_error"));
    }
    setBusy(false);
  };

  if (!tok)
    return (
      <div className="space-y-3">
        <label className="text-sm font-bold text-gray-700">{t("your_email")}</label>
        <input data-testid="email-input" className="craft-input" type="email" placeholder="you@mail.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="email-send-code-btn" className="craft-btn" disabled={busy || !email.includes("@")} onClick={send}>
          <Mail size={20} /> {t("send_code")}
        </button>
      </div>
    );
  return (
    <div className="space-y-3">
      <label className="text-sm font-bold text-gray-700">{t("enter_code")}</label>
      <input data-testid="email-code-input" className="craft-input text-center tracking-[0.5em] font-black text-xl" maxLength={6} inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
      <button data-testid="email-verify-btn" className="craft-btn" disabled={busy || code.length !== 6} onClick={check}>{t("verify")}</button>
      <button className="w-full text-xs font-bold text-gray-400 underline py-1" onClick={() => { setTok(null); setCode(""); }}>{t("resend")}</button>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const { L, t } = useLang();
  const common = { value: value ?? "", onChange: (e) => onChange(e.target.value) };
  const tid = `field-${field.key}`;
  switch (field.type) {
    case "textarea":
      return <textarea data-testid={tid} className="craft-input" placeholder={L(field.placeholder)} {...common} />;
    case "select":
      return (
        <select data-testid={tid} className="craft-input" {...common}>
          <option value="">{t("select")}</option>
          {(field.options || []).map((o) => <option key={o.value} value={o.value}>{L(o.label)}</option>)}
        </select>
      );
    case "radio":
      return (
        <div className="grid gap-2">
          {(field.options || []).map((o) => (
            <button key={o.value} type="button" data-testid={`${tid}-${o.value}`} onClick={() => onChange(o.value)}
              className={`h-12 rounded-2xl border-2 font-bold text-sm transition-colors ${value === o.value ? "border-[#FEBC10] bg-yellow-50" : "border-gray-200 bg-white"}`}>
              {L(o.label)}
            </button>
          ))}
        </div>
      );
    case "checkbox":
      return (
        <div className="grid gap-2">
          {(field.options || []).map((o) => {
            const arr = Array.isArray(value) ? value : [];
            const sel = arr.includes(o.value);
            return (
              <button key={o.value} type="button" data-testid={`${tid}-${o.value}`}
                onClick={() => onChange(sel ? arr.filter((x) => x !== o.value) : [...arr, o.value])}
                className={`h-12 rounded-2xl border-2 font-bold text-sm ${sel ? "border-[#FEBC10] bg-yellow-50" : "border-gray-200 bg-white"}`}>
                {L(o.label)}
              </button>
            );
          })}
        </div>
      );
    case "date": {
      // Дата рождения: минимальный возраст 10 лет (нельзя выбрать дату позже, чем сегодня − 10 лет)
      const isBirthday = /birth|dob|рожд|tug/i.test(`${field.key || ""} ${field.label?.ru || field.label || ""}`);
      const maxD = new Date(); maxD.setFullYear(maxD.getFullYear() - (isBirthday ? 10 : 0));
      const minD = isBirthday ? "1920-01-01" : undefined;
      const maxStr = maxD.toISOString().slice(0, 10);
      const clamp = (v) => (v && v > maxStr ? maxStr : v);
      return <input data-testid={tid} className="craft-input" type="date" max={maxStr} min={minD} {...common}
        onChange={(e) => onChange(clamp(e.target.value))} onBlur={(e) => { const v = clamp(e.target.value); if (v !== e.target.value) onChange(v); }} />;
    }
    case "number":
      return <input data-testid={tid} className="craft-input" type="number" placeholder={L(field.placeholder)} {...common} />;
    case "email":
      return <input data-testid={tid} className="craft-input" type="email" placeholder={L(field.placeholder) || "you@mail.com"} {...common} />;
    case "phone":
      return <input data-testid={tid} className="craft-input" type="tel" placeholder={L(field.placeholder) || "+998 90 123 45 67"} {...common} />;
    default:
      return <input data-testid={tid} className="craft-input" placeholder={L(field.placeholder)} {...common} />;
  }
}

function QuestionStep({ step, value, onChange }) {
  const { L } = useLang();
  const cfg = step.config || {};
  if (cfg.qtype === "rating") {
    const max = cfg.max || 5;
    return (
      <div className="flex justify-center gap-2 py-4" data-testid="rating-stars">
        {Array.from({ length: max }).map((_, i) => (
          <button key={i} data-testid={`star-${i + 1}`} onClick={() => onChange(i + 1)} className="transition-transform active:scale-90">
            <Star size={42} className={i < (value || 0) ? "fill-[#FEBC10] text-[#FEBC10]" : "text-gray-300"} />
          </button>
        ))}
      </div>
    );
  }
  return <FieldInput field={{ key: cfg.key || step.id, type: cfg.qtype || "radio", options: cfg.options, placeholder: cfg.placeholder }} value={value} onChange={onChange} />;
}

function WifiResult({ result, onRestart }) {
  const { t, L } = useLang();
  const [expired, setExpired] = useState(false);
  const wifi = result.wifi || {};
  const copy = () => { navigator.clipboard.writeText(wifi.password || ""); toast.success(t("copied")); };
  if (expired)
    return (
      <div className="text-center space-y-4 py-6" data-testid="wifi-expired">
        <TimerReset size={48} className="mx-auto text-[#FF0000]" />
        <h2 className="font-display font-black text-2xl">{t("access_expired")}</h2>
        <button data-testid="wifi-again-btn" className="craft-btn" onClick={onRestart}>{t("again")}</button>
      </div>
    );
  return (
    <div className="space-y-4" data-testid="wifi-result">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3"><CheckCircle2 size={34} className="text-green-600" /></div>
        <h2 className="font-display font-black text-2xl">{L(result.success?.title) || t("done")}</h2>
        <p className="text-sm text-gray-500 font-semibold mt-1">{L(result.success?.text)}</p>
      </div>
      <div className="bg-[#111] rounded-3xl p-5 text-white space-y-4">
        <div className="flex items-center gap-3"><Wifi className="text-[#FEBC10]" />
          <div><div className="text-xs text-gray-400 font-bold uppercase">{t("network")}</div>
            <div className="font-display font-black text-xl" data-testid="wifi-ssid">{wifi.ssid}</div></div>
        </div>
        <div className="flex items-center justify-between bg-white/10 rounded-2xl px-4 py-3">
          <div>
            <div className="text-xs text-gray-400 font-bold uppercase">{t("password")}</div>
            <div className="font-display font-black text-xl tracking-widest" data-testid="wifi-password">{wifi.password}</div>
          </div>
          <button data-testid="wifi-copy-password" onClick={copy} className="p-2.5 bg-[#FEBC10] text-black rounded-xl" aria-label={t("copy_pass")}><Copy size={18} /></button>
        </div>
        <div className="text-center pt-1">
          <div className="text-xs text-gray-400 font-bold uppercase mb-1">{t("access_time")}</div>
          <Countdown expiresAt={wifi.expires_at} onExpire={() => setExpired(true)} />
        </div>
      </div>
    </div>
  );
}

function PromoResult({ result }) {
  const { t, L } = useLang();
  const promo = result.promo || {};
  const copy = () => { navigator.clipboard.writeText(promo.code || ""); toast.success(t("copied")); };
  return (
    <div className="space-y-4 text-center" data-testid="promo-result">
      <div className="w-16 h-16 mx-auto rounded-full bg-yellow-100 flex items-center justify-center"><Gift size={34} className="text-[#EFAF04]" /></div>
      <h2 className="font-display font-black text-2xl">{L(result.success?.title) || t("your_promo")}</h2>
      <p className="text-sm text-gray-500 font-semibold">{L(result.success?.text)}</p>
      <div className="bg-[#FEBC10] rounded-3xl py-6 px-4">
        <div className="text-xs font-extrabold uppercase text-black/60">{t("your_promo")}</div>
        <div className="font-display font-black text-4xl tracking-widest" data-testid="promo-code">{promo.code}</div>
        {promo.expires && <div className="text-xs font-bold text-black/60 mt-1">{t("valid_until")}: {promo.expires}</div>}
      </div>
      {promo.conditions && <p className="text-xs text-gray-500">{L(promo.conditions)}</p>}
      <button data-testid="promo-copy-btn" className="craft-btn craft-btn-red" onClick={copy}><Copy size={18} /> {t("copy_promo")}</button>
    </div>
  );
}

function LoyaltyResult({ result }) {
  const { t, L } = useLang();
  const loy = result.loyalty || {};
  const dots = Array.from({ length: loy.required || 6 });
  const title = result.resumed
    ? `${t("welcome_back")}${result.first_name ? `, ${result.first_name}` : ""}!`
    : L(result.success?.title);
  return (
    <div className="space-y-4 text-center" data-testid="loyalty-result">
      <h2 className="font-display font-black text-2xl">{title}</h2>
      <p className="text-sm text-gray-500 font-semibold">{L(result.success?.text)}</p>
      <div className="craft-card p-5 space-y-3">
        <div className="text-xs font-extrabold uppercase text-gray-400">{t("progress")}</div>
        <div className="flex justify-center gap-2">
          {dots.map((_, i) => (
            <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${i < loy.count ? "bg-[#FEBC10]" : "bg-gray-200 text-gray-400"}`}>
              {i < loy.count ? "●" : "○"}
            </div>
          ))}
        </div>
        <div className="font-display font-black text-xl" data-testid="loyalty-progress">{t("visits_of", { a: loy.count, b: loy.required })}</div>
        {loy.reward_status === "available" ? (
          <div className="bg-green-50 rounded-2xl p-4 space-y-1">
            <PartyPopper className="mx-auto text-green-600" />
            <div className="font-black">{t("congrats")}</div>
            <div className="text-sm font-semibold text-gray-600">{L(loy.reward?.name)}</div>
            <div className="text-xs text-gray-400">{t("reward_ready")}</div>
          </div>
        ) : (
          <div className="text-sm font-bold text-gray-500">{t("visits_left", { a: (loy.required || 6) - (loy.count || 0) })}</div>
        )}
      </div>
      <div className="craft-card p-5 space-y-2">
        <div className="text-xs font-extrabold uppercase text-gray-400">{t("my_qr")}</div>
        <img data-testid="personal-qr" src={`${API}/qr/data?text=${loy.personal_code}`} alt="QR" className="w-40 h-40 mx-auto rounded-2xl" />
        <div className="font-display font-black text-xl tracking-widest" data-testid="personal-code">{loy.personal_code}</div>
        <p className="text-xs text-gray-400">{t("show_staff")}</p>
      </div>
    </div>
  );
}

function SurveyResult({ result }) {
  const { t, L } = useLang();
  const reward = result.reward;
  return (
    <div className="space-y-4 text-center" data-testid="survey-result">
      <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center"><CheckCircle2 size={34} className="text-green-600" /></div>
      <h2 className="font-display font-black text-2xl">{L(result.success?.title) || t("thanks")}</h2>
      <p className="text-sm text-gray-500 font-semibold">{L(result.success?.text)}</p>
      {reward?.enabled && (
        <div className="craft-card overflow-hidden">
          {reward.image_url && <img src={reward.image_url} alt="" className="w-full h-36 object-cover" />}
          <div className="p-5 space-y-2">
            <div className="text-xs font-extrabold uppercase text-gray-400">{t("your_gift")}</div>
            <div className="font-display font-black text-xl">{L(reward.name)}</div>
            <div className="text-sm text-gray-500">{L(reward.description)}</div>
            {reward.promo_code && <div className="bg-[#FEBC10] rounded-2xl py-3 font-display font-black text-2xl tracking-widest" data-testid="reward-promo-code">{reward.promo_code}</div>}
            {reward.expires && <div className="text-xs text-gray-400">{t("valid_until")}: {reward.expires}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function fmtClaimDate(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" }) + ", " + d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  } catch (e) { return iso; }
}

function ReviewStep({ campaign, value, onChange, claims }) {
  const { t, L } = useLang();
  const platforms = campaign.review?.platforms || [];
  const claimMap = {};
  (claims || []).forEach((c) => { claimMap[c.platform] = c.created_at; });
  const allDone = platforms.length > 0 && platforms.every((p) => claimMap[p.key]);
  return (
    <div className="space-y-2.5">
      <p className="text-sm font-bold text-gray-700">{t("review_choose")}</p>
      {platforms.map((p) => {
        const claimedAt = claimMap[p.key];
        if (claimedAt)
          return (
            <div key={p.key} data-testid={`review-platform-${p.key}-blocked`}
              className="w-full min-h-[56px] rounded-2xl border-2 border-gray-200 bg-gray-100 flex items-center gap-3 px-4 py-2 opacity-75">
              <Lock size={18} className="text-gray-400 shrink-0" />
              <div className="text-left">
                <div className="text-sm font-bold text-gray-500">{L(p.label)}</div>
                <div className="text-[11px] text-gray-400 font-semibold">{t("review_left_on")}: {fmtClaimDate(claimedAt)}</div>
              </div>
            </div>
          );
        return (
          <button key={p.key} type="button" data-testid={`review-platform-${p.key}`}
            onClick={() => { onChange(p.key); if (p.url) window.open(p.url, "_blank"); }}
            className={`w-full h-14 rounded-2xl border-2 font-bold flex items-center gap-3 px-4 transition-colors ${value === p.key ? "border-[#FEBC10] bg-yellow-50" : "border-gray-200 bg-white"}`}>
            <MapPin size={20} strokeWidth={2.5} className="text-[#FF0000]" /> {L(p.label)}
            {value === p.key && <CheckCircle2 size={18} className="ml-auto text-green-600" />}
          </button>
        );
      })}
      {allDone && <p className="text-xs font-bold text-[#FF0000] text-center pt-1" data-testid="review-all-done">{t("review_all_done")}</p>}
    </div>
  );
}

function ReviewResult({ result, campaign }) {
  const { t, L } = useLang();
  const rev = result.review || {};
  const platforms = campaign?.review?.platforms || [];
  const labelOf = (key) => { const p = platforms.find((x) => x.key === key); return p ? L(p.label) : key; };
  const claims = rev.claims || [];
  return (
    <div className="space-y-4 text-center" data-testid="review-result">
      <div className="w-16 h-16 mx-auto rounded-full bg-yellow-100 flex items-center justify-center"><Gift size={34} className="text-[#EFAF04]" /></div>
      <h2 className="font-display font-black text-2xl uppercase">{L(result.success?.title) || t("thanks")}</h2>
      <p className="text-sm text-gray-500 font-semibold">{L(result.success?.text)}</p>
      {rev.already_claimed && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 text-left" data-testid="review-already-claimed">
          <div className="text-sm font-bold text-[#FF0000]">{t("review_already")}</div>
          {rev.claimed_at && (
            <div className="text-xs text-gray-500 font-semibold mt-1.5">
              {labelOf(rev.platform)} — {t("review_left_on").toLowerCase()}: {fmtClaimDate(rev.claimed_at)}
            </div>
          )}
        </div>
      )}
      {!rev.already_claimed && rev.reward && (
        <div className="bg-gray-50 rounded-3xl overflow-hidden">
          {rev.reward.image_url && <img src={rev.reward.image_url} alt="" className="w-full h-32 object-cover" />}
          <div className="p-4 space-y-1">
            <div className="text-xs font-extrabold uppercase text-gray-400">{t("your_gift")}</div>
            <div className="font-display font-black text-lg">{L(rev.reward.name)}</div>
            <div className="text-xs text-gray-500">{L(rev.reward.description)}</div>
          </div>
        </div>
      )}
      {claims.length > 0 && (
        <div className="bg-white border-2 border-gray-100 rounded-2xl p-4 text-left" data-testid="review-claims-history">
          <div className="text-xs font-extrabold uppercase text-gray-400 mb-2">{t("review_history")}</div>
          <div className="space-y-2">
            {claims.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold flex items-center gap-1.5"><CheckCircle2 size={15} className="text-green-600 shrink-0" /> {labelOf(c.platform)}</span>
                <span className="text-xs text-gray-500 font-semibold whitespace-nowrap">{fmtClaimDate(c.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {!rev.already_claimed && <p className="text-xs text-gray-400 font-semibold">{t("review_show_staff")}</p>}
    </div>
  );
}

export default function CampaignFlow() {
  const { slug } = useParams();
  const { t, L, lang } = useLang();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [data, setData] = useState({});
  const [legal, setLegal] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewClaims, setReviewClaims] = useState([]);

  const load = async () => {
    setError(null); setResult(null); setIdx(0); setSession(null); setVerifiedData(null); setData({}); setReviewClaims([]);
    try {
      const { data: p } = await api.get(`/public/campaign/${slug}`);
      setPayload(p);
      // Coffee/loyalty campaigns remember the account; other campaigns always require re-registration
      const camp = p?.campaign;
      if (camp?.type === "loyalty") {
        const saved = localStorage.getItem(`craft_loyalty_${camp.id}`);
        if (saved) {
          try {
            const { data: st } = await api.get(`/public/loyalty/${saved}`);
            const prog = (st.programs || []).find((x) => x.campaign_id === camp.id);
            if (prog) {
              setResult({
                type: "loyalty", resumed: true, first_name: st.first_name, success: camp.success || {},
                loyalty: { count: prog.count, required: prog.required, reward_status: prog.reward_status, reward: prog.reward, personal_code: st.personal_code },
              });
              return;
            }
            localStorage.removeItem(`craft_loyalty_${camp.id}`);
          } catch (e) {
            localStorage.removeItem(`craft_loyalty_${camp.id}`);
          }
        }
      }
      // Возврат из Telegram: ?rs=<session_id> — сессия уже с подтверждённым телефоном,
      // пропускаем шаг идентификации и продолжаем с того места, где остановились
      const rs = new URLSearchParams(window.location.search).get("rs");
      if (rs && camp) {
        try {
          const { data: vs } = await api.get(`/public/session/${rs}/verification`);
          if (vs.verified) {
            setSession(rs);
            setVerifiedData({ phone: vs.phone });
            const stepsArr = camp.steps || [];
            const idIdx = stepsArr.findIndex((s) => s.type === "identify");
            if (idIdx >= 0) setIdx(Math.min(idIdx + 1, Math.max(0, stepsArr.length - 1)));
            try { window.history.replaceState({}, "", `/c/${slug}`); } catch (e2) { /* noop */ }
          }
        } catch (e3) { /* noop */ }
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d === "campaign_disabled" ? t("qr_disabled") : t("not_found"));
    }
  };
  useEffect(() => { load(); }, [slug]);

  const campaign = payload?.campaign;
  const steps = campaign?.steps || [];
  const step = steps[idx];
  const hasForm = useMemo(() => steps.some((s) => s.type === "form" || s.type === "question"), [steps]);

  // Load claimed review platforms (per-customer block) once the review step is reached
  useEffect(() => {
    if (step?.type === "review" && session) {
      api.get(`/public/session/${session}/review-status`)
        .then(({ data: rs }) => setReviewClaims(rs.claims || []))
        .catch(() => {});
    }
  }, [step?.id, step?.type, session]);

  const next = async (stepData = {}) => {
    setBusy(true);
    try {
      let sid = session;
      if (!sid) {
        const { data: s } = await api.post(`/public/campaign/${slug}/start`, { language: lang });
        sid = s.session_id; setSession(sid);
      }
      const merged = { ...data, ...stepData };
      setData(merged);
      // Send the FULL merged data (form fields are stored in `data` state, not in stepData) —
      // otherwise email/gender/birthday etc. never reach the session and are not saved to CRM
      await api.post(`/public/session/${sid}/submit`, { step_id: step?.id, data: merged, legal_consent: legal, marketing_consent: marketing });
      if (idx + 1 < steps.length) {
        setIdx(idx + 1);
      } else {
        const { data: res } = await api.post(`/public/session/${sid}/complete`, {});
        setResult(res);
        if (campaign?.type === "loyalty" && res?.loyalty?.personal_code) {
          localStorage.setItem(`craft_loyalty_${campaign.id}`, res.loyalty.personal_code);
        }
      }
    } catch (e) { toast.error(t("server_error")); }
    setBusy(false);
  };

  if (error)
    return (
      <Shell><Header />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3">
          <AlertTriangle size={48} className="text-[#FF0000]" />
          <h2 className="font-display font-black text-2xl" data-testid="flow-error">{error}</h2>
        </div>
        <CraftFooter />
      </Shell>
    );
  if (!campaign) return <Shell><Header /><div className="flex-1 flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#FEBC10] border-t-transparent rounded-full animate-spin" /></div></Shell>;

  const ads = payload.ads || {};
  // Only campaign-level banners (selected in admin builder) are shown in the top/bottom slots on ALL steps
  const campTop = campaign.banner_top_id ? ads[campaign.banner_top_id] : null;
  const campBottom = campaign.banner_bottom_id ? ads[campaign.banner_bottom_id] : null;

  if (result)
    return (
      <Shell><Header />
        <div className="px-5 pb-4 step-enter flex-1">
          {campTop && <div className="pb-4"><BannerCard ad={campTop} campaignId={campaign.id} compact /></div>}
          <div className="craft-card p-6">
            {result.type === "wifi" && <WifiResult result={result} onRestart={load} />}
            {result.type === "promo" && <PromoResult result={result} />}
            {result.type === "survey" && <SurveyResult result={result} />}
            {result.type === "loyalty" && <LoyaltyResult result={result} />}
            {result.type === "review" && <ReviewResult result={result} campaign={campaign} />}
            {(result.type === "lead" || result.type === "custom") && <SurveyResult result={result} />}
          </div>
          {result.type === "loyalty" && (
            <button data-testid="loyalty-switch-account" onClick={() => { localStorage.removeItem(`craft_loyalty_${campaign.id}`); load(); }}
              className="w-full text-xs font-bold text-gray-400 underline py-3">
              {t("switch_account")}
            </button>
          )}
          {result.success?.button_text && result.success?.button_link && (
            <a data-testid="success-cta" href={result.success.button_link} target="_blank" rel="noreferrer" className="craft-btn craft-btn-red mt-4">{L(result.success.button_text)}</a>
          )}
          {campBottom && <div className="mt-4"><BannerCard ad={campBottom} campaignId={campaign.id} compact /></div>}
        </div>
        <CraftFooter />
      </Shell>
    );

  const stepAd = step?.banner_id ? ads[step.banner_id] : null; // content of an explicit "advertisement" step only
  const adTop = campTop;
  const adBottom = campBottom;
  const isWelcome = step?.type === "welcome";
  const marketingRequired = true;
  const isForm = step?.type === "form";
  const fields = (step?.config?.fields || []).filter((f) => f.visible !== false);
  const filled = (v) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  const requiredOk = !isForm || fields.every((f) => !f.required || filled(data[f.key]));
  const formNeedsConsent = isForm || (step?.type === "question" && idx === steps.findIndex((s) => s.type === "question"));
  const questionVal = step?.type === "question" ? data[step.config?.key || step.id] : null;
  const questionRequired = step?.type === "question" && step.required !== false;

  return (
    <Shell>
      <Header />
      {adTop && <div className="px-5 pb-4"><BannerCard ad={adTop} campaignId={campaign.id} /></div>}
      <div className="px-5 flex-1">
        <div className="craft-card p-6 step-enter" key={step.id} data-testid={`step-${step.type}`}>
          {!isWelcome && <LabeledStepper steps={steps} activeIndex={Math.max(0, steps.filter((s) => s.type !== "welcome").findIndex((s) => s.id === step.id))} />}
          {isWelcome ? (
            <div className="flex items-start gap-4 mb-2">
              {(() => { const Icon = TYPE_ICON[campaign.type] || Gift; return (
                <div className="w-14 h-14 rounded-2xl bg-yellow-50 flex items-center justify-center shrink-0"><Icon size={28} strokeWidth={2.5} className="text-[#EFAF04]" /></div>
              ); })()}
              <div>
                <h1 className="font-display font-black text-xl uppercase leading-tight">{L(step.title)}</h1>
                {L(step.text) && <p className="text-sm text-gray-500 font-semibold mt-1">{L(step.text)}</p>}
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-display font-black text-2xl leading-tight mb-1">{L(step.title)}{questionRequired && <span className="text-[#FF0000]"> *</span>}</h1>
              {L(step.text) && <p className="text-sm text-gray-500 font-semibold mb-4">{L(step.text)}</p>}
            </>
          )}
          {isWelcome && <LabeledStepper steps={steps} activeIndex={0} />}

          {step.type === "identify" && !session && (
            <IdentifyStarter onStart={async () => { const { data: s } = await api.post(`/public/campaign/${slug}/start`, { language: lang }); setSession(s.session_id); }} />
          )}
          {step.type === "identify" && session && campaign.identification?.method === "telegram" && (
            <TelegramIdentify session={session} telegram={payload.telegram}
              onVerified={(v) => { setVerifiedData(v); setTimeout(() => next(v), 600); }} />
          )}
          {step.type === "identify" && session && campaign.identification?.method === "email" && (
            <EmailIdentify session={session} onVerified={(v) => { setVerifiedData(v); next(v); }} />
          )}

          {isForm && (
            <div className="space-y-4">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="text-sm font-bold text-gray-700 mb-1.5 block">
                    {L(f.label)} {f.required && <span className="text-[#FF0000]">*</span>}
                  </label>
                  <FieldInput field={f} value={data[f.key]} onChange={(v) => setData({ ...data, [f.key]: v })} />
                </div>
              ))}
            </div>
          )}

          {step.type === "question" && (
            <QuestionStep step={step} value={questionVal} onChange={(v) => setData({ ...data, [step.config?.key || step.id]: v })} />
          )}

          {step.type === "review" && (
            <ReviewStep campaign={campaign} value={data.review_platform} claims={reviewClaims} onChange={(v) => setData({ ...data, review_platform: v })} />
          )}

          {step.type === "advertisement" && stepAd && !isWelcome && <BannerCard ad={stepAd} campaignId={campaign.id} />}
          {step.type === "custom_text" && null}

          {formNeedsConsent && (
            <div className="mt-5 space-y-2.5">
              <label className="flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer">
                <input data-testid="legal-consent-checkbox" type="checkbox" checked={legal} onChange={(e) => setLegal(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FEBC10]" />
                <span>{t("legal_consent")}</span>
              </label>
              <label className="flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer">
                <input data-testid="marketing-consent-checkbox" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FEBC10]" />
                <span>{t("marketing_consent")}{marketingRequired && <span className="text-[#FF0000] font-bold"> *</span>}</span>
              </label>
            </div>
          )}

          {step.type !== "identify" && (
            <div className="mt-5 space-y-2">
              <button data-testid="step-continue-btn" className="craft-btn"
                disabled={busy || (isForm && (!requiredOk || !legal)) || (formNeedsConsent && marketingRequired && !marketing) || (questionRequired && !filled(questionVal)) || (step.type === "review" && !data.review_platform)}
                onClick={() => next({})}>
                {step.type === "review" ? t("review_done") : t("continue")}
              </button>
              {isForm && !requiredOk && <p className="text-xs text-center text-[#FF0000] font-bold">{t("fill_required")}</p>}
              {step.required === false && (
                <button data-testid="step-skip-btn" className="w-full text-xs font-bold text-gray-400 py-1" onClick={() => next({})}>{t("skip")}</button>
              )}
            </div>
          )}
          {isWelcome && (
            <div className="flex items-center justify-center gap-1.5 mt-3 text-gray-400 text-xs font-semibold"><Lock size={12} /> {t("data_safe")}</div>
          )}
        </div>
        {adBottom && <div className="mt-4"><BannerCard ad={adBottom} campaignId={campaign.id} compact /></div>}
        {isWelcome && <FeatureRow />}
      </div>
      <CraftFooter />
    </Shell>
  );
}

function IdentifyStarter({ onStart }) {
  useEffect(() => { onStart(); }, []);
  return null;
}
